/**
 * MissedCallStore — IndexedDB-backed per-thread history of missed calls.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/MissedCallStore.swift`.
 *
 * ## Design notes
 *
 * ### callId dedup
 * Logging the same `callId` twice is a no-op. The second `log()` call
 * returns `{ inserted: false }` without changing the history or the unread
 * counter. This mirrors iOS's `Set<String>` dedup guard.
 *
 * ### High-water-mark unread counter
 * Each thread maintains a `highWaterCallId` — the `callId` of the most
 * recent entry that was present when the caller last called `markRead()`.
 * `unreadCount(threadId)` counts entries whose `receivedAtMs` is strictly
 * greater than the `receivedAtMs` of the high-water entry.
 *
 * If `markRead()` has never been called for a thread, all entries are
 * considered unread.
 *
 * ### Persistence
 * All records are stored in IndexedDB. The database name is supplied at
 * construction time so tests can use isolated ephemeral databases.
 *
 * ### No encryption at the IndexedDB layer
 * iOS uses `.completeFileProtection`; the web equivalent is "SubtleCrypto
 * with non-extractable CryptoKey + IndexedDB" — see the platform-gaps table
 * in `Signal_standard_web.md`. This store stores plaintext in IDB exactly as
 * every other web E2EE store does; the gap is documented.
 *
 * ### Per-thread isolation
 * All IDB keys are namespaced `<DB_PREFIX><threadId>:<callId>` so entries
 * from different threads never collide.
 */

import type { MissedCallEvent } from "./MissedCallEvent";

// ---------------------------------------------------------------------------
// IDB key / store constants
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether:missed-calls:v1";
const STORE_CALLS = "calls";
const STORE_META = "meta";
const DB_VERSION = 1;

// ---------------------------------------------------------------------------
// IDB bootstrap helper
// ---------------------------------------------------------------------------

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Object store for call records; keyed by compound "threadId\x00callId"
      if (!db.objectStoreNames.contains(STORE_CALLS)) {
        const store = db.createObjectStore(STORE_CALLS, { keyPath: "_key" });
        // Index by threadId so we can query all calls for a thread efficiently
        store.createIndex("byThread", "threadId", { unique: false });
      }
      // Object store for per-thread metadata (high-water-mark)
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: "threadId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function callKey(threadId: string, callId: string): string {
  return `${threadId}\x00${callId}`;
}

// ---------------------------------------------------------------------------
// Stored record shape
// ---------------------------------------------------------------------------

interface StoredCallRecord {
  /** Compound IDB key: "threadId\x00callId" */
  _key: string;
  threadId: string;
  callId: string;
  callerUserId: string;
  callerDisplayHint: string;
  receivedAtMs: number;
}

interface StoredMeta {
  threadId: string;
  /** callId of the high-water entry (most recent entry present at last markRead) */
  hwCallId: string;
  /** receivedAtMs of the high-water entry (cached to avoid a second lookup) */
  hwReceivedAtMs: number;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface LogResult {
  /** `true` if the entry was newly inserted; `false` if the callId was a dup. */
  readonly inserted: boolean;
}

// ---------------------------------------------------------------------------
// MissedCallStore
// ---------------------------------------------------------------------------

/**
 * Per-thread missed-call history backed by IndexedDB.
 *
 * Construct one per JS context; a single instance can handle all threads.
 * The `dbName` parameter allows test isolation.
 */
export class MissedCallStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // Internal DB getter (lazy open, cached)
  // -------------------------------------------------------------------------

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // log
  // -------------------------------------------------------------------------

  /**
   * Persist a `MissedCallEvent` for its thread.
   *
   * If a record with the same `callId` already exists in the thread, this is
   * a no-op and returns `{ inserted: false }`.
   *
   * Otherwise the record is stored and returns `{ inserted: true }`. The
   * unread counter implicitly increments because `markRead` has not been
   * called for the new entry yet.
   */
  async log(event: MissedCallEvent): Promise<LogResult> {
    const db = await this.db();
    const key = callKey(event.threadId, event.callId);

    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_CALLS], "readwrite");
      const store = tx.objectStore(STORE_CALLS);

      const getReq = store.get(key);
      getReq.onsuccess = () => {
        if (getReq.result !== undefined) {
          // callId already present — dedup, no-op
          tx.abort();
          resolve({ inserted: false });
          return;
        }

        const record: StoredCallRecord = {
          _key: key,
          threadId: event.threadId,
          callId: event.callId,
          callerUserId: event.callerUserId,
          callerDisplayHint: event.callerDisplayHint,
          receivedAtMs: event.receivedAtMs,
        };
        const putReq = store.put(record);
        putReq.onsuccess = () => resolve({ inserted: true });
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
      tx.onerror = () => {
        // tx.abort() causes an error event — suppress it if we already resolved
      };
    });
  }

  // -------------------------------------------------------------------------
  // history
  // -------------------------------------------------------------------------

  /**
   * Return all missed-call events for `threadId`, sorted ascending by
   * `receivedAtMs` (oldest first).
   */
  async history(threadId: string): Promise<MissedCallEvent[]> {
    const db = await this.db();

    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_CALLS], "readonly");
      const store = tx.objectStore(STORE_CALLS);
      const index = store.index("byThread");

      const results: StoredCallRecord[] = [];
      const req = index.openCursor(IDBKeyRange.only(threadId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          results.push(cursor.value as StoredCallRecord);
          cursor.continue();
        } else {
          results.sort((a, b) => a.receivedAtMs - b.receivedAtMs);
          resolve(
            results.map((r) => ({
              callId: r.callId,
              threadId: r.threadId,
              callerUserId: r.callerUserId,
              callerDisplayHint: r.callerDisplayHint,
              receivedAtMs: r.receivedAtMs,
            })),
          );
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // markRead
  // -------------------------------------------------------------------------

  /**
   * Mark all current entries for `threadId` as read by advancing the
   * high-water mark to the most-recently-received entry.
   *
   * After this call, `unreadCount(threadId)` returns 0 (until new entries
   * are logged).
   */
  async markRead(threadId: string): Promise<void> {
    const db = await this.db();
    const entries = await this.history(threadId);
    if (entries.length === 0) return;

    const newest = entries[entries.length - 1]!;

    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_META], "readwrite");
      const store = tx.objectStore(STORE_META);
      const meta: StoredMeta = {
        threadId,
        hwCallId: newest.callId,
        hwReceivedAtMs: newest.receivedAtMs,
      };
      const req = store.put(meta);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // unreadCount
  // -------------------------------------------------------------------------

  /**
   * Count missed-call entries for `threadId` that arrived after the
   * high-water mark. If `markRead` has never been called, all entries are
   * unread.
   */
  async unreadCount(threadId: string): Promise<number> {
    const db = await this.db();

    // Fetch high-water meta
    const meta = await new Promise<StoredMeta | undefined>(
      (resolve, reject) => {
        const tx = db.transaction([STORE_META], "readonly");
        const store = tx.objectStore(STORE_META);
        const req = store.get(threadId);
        req.onsuccess = () => resolve(req.result as StoredMeta | undefined);
        req.onerror = () => reject(req.error);
      },
    );

    const hwMs = meta?.hwReceivedAtMs ?? -1;
    const entries = await this.history(threadId);
    return entries.filter((e) => e.receivedAtMs > hwMs).length;
  }

  // -------------------------------------------------------------------------
  // deleteAll (for testing / account wipe)
  // -------------------------------------------------------------------------

  /**
   * Wipe all records for `threadId` and reset its high-water mark.
   */
  async deleteAll(threadId: string): Promise<void> {
    const db = await this.db();
    const entries = await this.history(threadId);

    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_CALLS, STORE_META], "readwrite");

      const callStore = tx.objectStore(STORE_CALLS);
      for (const entry of entries) {
        callStore.delete(callKey(threadId, entry.callId));
      }

      const metaStore = tx.objectStore(STORE_META);
      metaStore.delete(threadId);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
